import { create } from "zustand";
import type { AlgorithmOperation } from "@algoquest/schemas";
import type { SortViewState } from "../algorithms/sorting/SortController";

/**
 * sortAlgorithmStore (Phase 05, unified in Phase 08): a read-only Zustand
 * mirror of whichever SortController is loaded, written only by
 * GameRuntime's callbacks. Through Phase 07 this held Bubble Sort's own
 * `BubbleSortDomainState` directly, with `selectionSortAlgorithmStore`
 * and `insertionSortAlgorithmStore` as separate parallel stores for the
 * other two algorithms — exactly mirroring `searchAlgorithmStore`'s shape
 * from before Phase 04 unified Searching. Now that all three sorting
 * algorithms speak the same `SortViewState` shape via `SortController`,
 * this single store replaces all three (the other two stores are
 * retired), mirroring `searchAlgorithmStore.ts` exactly.
 */
interface SortAlgorithmState {
  view: SortViewState | null;
  trace: AlgorithmOperation[];
  rejectedHintIndex: number | null;
  decisionRejected: boolean;
  setView: (view: SortViewState) => void;
  appendTrace: (operations: AlgorithmOperation[]) => void;
  setRejectedHint: (index: number | null) => void;
  setDecisionRejected: (rejected: boolean) => void;
  reset: (view: SortViewState) => void;
}

export const useSortAlgorithmStore = create<SortAlgorithmState>((set) => ({
  view: null,
  trace: [],
  rejectedHintIndex: null,
  decisionRejected: false,
  setView: (view) => set({ view }),
  appendTrace: (operations) => set((state) => ({ trace: [...state.trace, ...operations] })),
  setRejectedHint: (rejectedHintIndex) => set({ rejectedHintIndex }),
  setDecisionRejected: (decisionRejected) => set({ decisionRejected }),
  reset: (view) => set({ view, trace: [], rejectedHintIndex: null, decisionRejected: false }),
}));
